import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  SYSTEM_LIGHT_QUERY,
  applyTheme,
  readPref,
  resolve,
  writePref,
  type Theme,
  type ThemePref,
} from './theme';

interface ThemeContextValue {
  pref: ThemePref;
  theme: Theme;
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = use(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // The boot script in index.html has already applied this to <html> before first paint.
  // Reading it again here keeps React's state and the DOM in agreement from render one.
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [theme, setTheme] = useState<Theme>(() => resolve(readPref()));

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    writePref(next);
    const resolved = resolve(next);
    setTheme(resolved);
    applyTheme(resolved);
  }, []);

  // Only listen while the user has actually asked to follow the system. Outside that,
  // a system change must not move a theme they pinned.
  useEffect(() => {
    if (pref !== 'system') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(SYSTEM_LIGHT_QUERY);
    } catch {
      return;
    }
    const onChange = () => {
      const resolved: Theme = mql.matches ? 'light' : 'dark';
      setTheme(resolved);
      applyTheme(resolved);
    };
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [pref]);

  const value = useMemo<ThemeContextValue>(() => ({ pref, theme, setPref }), [pref, theme, setPref]);
  return <ThemeContext value={value}>{children}</ThemeContext>;
}
